import { useState, useMemo, useEffect } from 'react';

// Hook to handle single or multiple selection logic for tabs/places.
// - multi: boolean, if true works with array selection, otherwise single-selection string
// - defaultValue: 'All' by default
// - places: array of available places used to compute selectedPlaces
export default function useSelection({ multi = true, defaultValue = 'All', places = [] } = {}) {
  const [selected, setSelected] = useState(multi ? [defaultValue] : defaultValue);

  // Auto-select first place when places load (only for single-select mode)
  useEffect(() => {
    if (!multi && selected === 'All' && places && places.length > 0) {
      setSelected(places[0]);
    }
  }, [places, multi, selected]);

  // Reset selection when places change completely (e.g., switching between Cities/States)
  useEffect(() => {
    if (!multi && places && places.length > 0) {
      // If current selection is not in new places list, reset to first place
      if (selected !== 'All' && !places.includes(selected)) {
        setSelected(places[0]);
      }
    }
  }, [places, multi, selected]);

  const handleClick = (item) => {
    if (multi) {
      if (item === 'All') return setSelected(["All"]);
      setSelected(prev => {
        const withoutAll = prev.filter(x => x !== 'All');
        const has = withoutAll.includes(item);
        const next = has ? withoutAll.filter(x => x !== item) : [...withoutAll, item];
        return next.length ? next : ['All'];
      });
      return;
    }

    // single-select behavior
    if (item === 'All') return setSelected('All');
    setSelected(prev => (prev === item ? 'All' : item));
  };

  const selectedPlaces = useMemo(() => {
    if (multi) {
      return Array.isArray(selected) && selected.includes('All') ? (places || []) : selected;
    }
    return selected === 'All' ? (places || []) : [selected];
  }, [selected, places, multi]);

  return { selected, setSelected, selectedPlaces, handleClick };
}
